<template>
  <AsyncModal
    :show="show"
    :header-icon="['fas', 'fa-key']"
    :header-text="clientRef != null ? `Client ${clientRef.name}` : ''"
    :async-function="updateClient"
    :is-valid="isValid"
    confirm-text="Update"
    @close="emit('close')"
    @done="onDone"
  >
    <template #content>
      <div class="tw-mb-8 tw-px-4">
        <h3>Information</h3>
        <form id="form">
          <GpTextInput
            readonly
            transparent
            label="Client ID"
            :model-value="clientRef.id"
            name="id"
            input-id="client-id"
          >
            <template #actions>
              <GpInputBtn :icon="['fas', 'fa-copy']" @click="copyToClipboard(clientRef.id)" />
            </template>
          </GpTextInput>
          <GpTextInput
            label="Name*"
            v-model="clientRef.name"
            name="name"
            required
            input-id="client-name"
          />
          <GpTextInput
            label="Description*"
            v-model="clientRef.description"
            name="description"
            required
            input-id="client-description"
          />
        </form>
        <br />
        <h3>Service Account</h3>
        <GpAlert
          alert-type="warning"
          size="sm"
          class="tw-mt-4"
          message="You can use the service account user ID to invite this user to projects. This allows you to access the API with the client credentials."
        />

        <div class="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-leading-none">
          <GpAvatar
            class="tw-mr-4"
            :src="clientRef.serviceAccount.avatarUrl"
            :alt="clientRef.serviceAccount.username"
            rounded
            custom-size
            style="--gp-avatar-size: 32px"
          />

          <p>
            {{ clientRef.serviceAccount.username }}
            <br />
            <small>User ID: {{ clientRef.serviceAccount.id }}</small>
          </p>
        </div>
      </div>
    </template>
  </AsyncModal>
</template>

<script lang="ts">
import { GpAlert, GpAvatar, GpInputBtn, GpTextInput } from '@gportal/uikit';
import { useClipboard } from '@vueuse/core';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import type { Client } from 'gpcore-grpc/gpcore/api/auth/v1/client_pb';
import AsyncModal from 'gpcore-uikit/components/new/AsyncModal.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { type PropType, computed, defineComponent, ref, toRef, watch, watchEffect } from 'vue';

export default defineComponent({
  components: {
    AsyncModal,
    GpTextInput,
    GpAlert,
    GpAvatar,
    GpInputBtn,
  },
  emits: ['close', 'updated'],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    client: {
      type: Object as PropType<Client>,
      default: null,
    },
  },
  setup(props, { emit }) {
    const userStore = useUserStore();
    const { addNotification } = useNotifications();

    const propsClient = toRef(props, 'client');
    const clientRef = ref<Client>();
    watchEffect(() => {
      if (propsClient.value != null) {
        clientRef.value = propsClient.value;
      } else {
        clientRef.value = null;
      }
    });

    const isValid = computed(
      () =>
        clientRef.value != null &&
        clientRef.value.name !== '' &&
        clientRef.value.description !== '',
    );

    const updateClient = () => {
      if (!isValid.value) {
        return undefined;
      }

      return userStore.updateClient(clientRef.value);
    };
    const onDone = () => {
      emit('updated');
      addNotification({
        type: 'success',
        message: 'The Client has been updated.',
      });
    };

    watch(
      () => props.client,
      () => {
        if (props.client != null) {
          clientRef.value = props.client;
        } else {
          clientRef.value = null;
        }
      },
    );

    const { copy: copyToClipboard } = useClipboard();

    return {
      updateClient,
      onDone,
      isValid,
      clientRef,
      emit,
      copyToClipboard,
    };
  },
});
</script>
